/**
 * BradfordFactorWidget.jsx
 * 
 * Compact widget showing Bradford Factor summary for team dashboard.
 * Displays alert count and employees requiring attention.
 * 
 * Usage:
 * <BradfordFactorWidget onClick={() => navigate('/bradford-factor')} />
 */

import React, { useState, useEffect } from 'react';
import api from './axiosConfig';

// Icons
const Icons = {
  AlertTriangle: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
      <line x1="12" y1="9" x2="12" y2="13"></line>
      <line x1="12" y1="17" x2="12.01" y2="17"></line>
    </svg>
  ),
  ChevronRight: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>
  ),
  Check: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12"></polyline>
    </svg>
  ),
  TrendingUp: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"></polyline>
      <polyline points="17,6 23,6 23,12"></polyline>
    </svg>
  ),
};

// Level colors
const LEVEL_COLORS = {
  green: { color: '#22c55e', bg: '#dcfce7' },
  amber: { color: '#f59e0b', bg: '#fef3c7' },
  orange: { color: '#f97316', bg: '#ffedd5' },
  red: { color: '#ef4444', bg: '#fee2e2' },
  critical: { color: '#991b1b', bg: '#fecaca' },
};

const BradfordFactorWidget = ({ onClick, style = {} }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ alerts: [], summary: {} });
  const [hover, setHover] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [alertsResponse, teamResponse] = await Promise.all([
        api.get('/bradford/alerts'),
        api.get('/bradford/team'),
      ]);
      
      setData({
        alerts: alertsResponse.data.alerts || [],
        summary: teamResponse.data.summary || {},
      });
    } catch (err) {
      console.error('Failed to load Bradford data:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasAlerts = data.alerts.length > 0;
  const criticalCount = data.summary.critical || 0;
  const redCount = data.summary.red || 0;
  const warningCount = criticalCount + redCount + (data.summary.orange || 0) + (data.summary.amber || 0);

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        padding: '20px',
        ...style,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: '#f1f5f9',
          }} />
          <div>
            <div style={{ height: '16px', width: '120px', background: '#f1f5f9', borderRadius: '4px' }} />
            <div style={{ height: '12px', width: '80px', background: '#f1f5f9', borderRadius: '4px', marginTop: '8px' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: hover ? '0 4px 14px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.08)',
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        transform: hover ? 'translateY(-1px)' : 'none',
        border: hasAlerts ? '1px solid #fecaca' : (hover ? '1px solid #99f6e4' : '1px solid transparent'),
        ...style,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            background: hasAlerts ? '#fee2e2' : '#dcfce7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: hasAlerts ? '#ef4444' : '#22c55e',
          }}>
            {hasAlerts ? <Icons.AlertTriangle /> : <Icons.Check />}
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '15px', color: '#1e293b' }}>
              Bradford Factor
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
              {hasAlerts ? `${warningCount} need attention` : 'All clear'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', color: hover ? '#0d9488' : '#64748b', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}>
          View dashboard
          <Icons.ChevronRight />
        </div>
      </div>

      {/* Level summary bars */}
      {hasAlerts && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          {criticalCount > 0 && (
            <div style={{
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              background: LEVEL_COLORS.critical.bg,
              color: LEVEL_COLORS.critical.color,
            }}>
              {criticalCount} critical
            </div>
          )}
          {redCount > 0 && (
            <div style={{
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              background: LEVEL_COLORS.red.bg,
              color: LEVEL_COLORS.red.color,
            }}>
              {redCount} serious
            </div>
          )}
          {(data.summary.orange || 0) > 0 && (
            <div style={{
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              background: LEVEL_COLORS.orange.bg,
              color: LEVEL_COLORS.orange.color,
            }}>
              {data.summary.orange} warning
            </div>
          )}
        </div>
      )}

      {/* Top alerts preview */}
      {data.alerts.length > 0 && (
        <div style={{ marginTop: '12px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
          {data.alerts.slice(0, 3).map((alert, idx) => (
            <div key={alert.employee_id} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 0',
              borderBottom: idx < Math.min(data.alerts.length - 1, 2) ? '1px solid #f8fafc' : 'none',
            }}>
              <div>
                <span style={{ fontSize: '13px', fontWeight: '500', color: '#334155' }}>
                  {alert.name}
                </span>
                <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '8px' }}>
                  {alert.spells}s • {alert.days}d
                </span>
              </div>
              <span style={{
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600',
                background: LEVEL_COLORS[alert.level]?.bg,
                color: LEVEL_COLORS[alert.level]?.color,
              }}>
                {alert.score}
              </span>
            </div>
          ))}
          {data.alerts.length > 3 && (
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>
              +{data.alerts.length - 3} more
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BradfordFactorWidget;